'use client'
import React, { forwardRef } from 'react'
import sanitizeHtml from 'sanitize-html'
import { cn } from '~/lib'

interface SafeHTMLProps extends React.HTMLAttributes<HTMLElement> {
  html: string
  as?: React.ElementType
  sanitizeOptions?: sanitizeHtml.IOptions
}

const defaultSanitizeOptions: sanitizeHtml.IOptions = {
  allowedTags: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    'i',
    'b',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'img',
    'blockquote',
    'hr',
    'svg',
    'path',
    'circle',
    'rect',
    'line',
    'polyline',
    'g',
    'defs',
    'use',
  ],
  allowedAttributes: {
    '*': ['class', 'style'],
    a: ['href', 'target', 'rel'],
    img: ['src', 'alt', 'width', 'height', 'loading'],
    svg: ['viewBox', 'width', 'height', 'xmlns', 'fill', 'stroke'],
    path: ['d', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin'],
    circle: ['cx', 'cy', 'r', 'fill', 'stroke', 'stroke-width'],
    rect: ['x', 'y', 'width', 'height', 'fill', 'stroke', 'stroke-width'],
    line: ['x1', 'y1', 'x2', 'y2', 'stroke', 'stroke-width'],
    polyline: ['points', 'fill', 'stroke', 'stroke-width'],
    g: ['transform', 'fill', 'stroke'],
    use: ['href', 'xlink:href'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  allowedClasses: {
    '*': ['*'],
  },
}

const SafeHTML = forwardRef<HTMLDivElement, SafeHTMLProps>(
  (
    { html, as: Component = 'div', className, sanitizeOptions = defaultSanitizeOptions, ...props },
    ref
  ) => {
    if (!html?.trim()) return null

    const sanitizedHTML = sanitizeHtml(html, sanitizeOptions)
    if (!sanitizedHTML?.trim()) return null

    return (
      <Component
        ref={ref}
        className={cn('safe-html', className)}
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        {...props}
      />
    )
  }
)

SafeHTML.displayName = 'SafeHTML'

export default SafeHTML
